import { Reducer } from 'redux'
import { createReducer } from 'reduxsauce'
import { PatientMetricsTypes, IPatientMetricsState } from './types'
import { failure, IActionType, IPaginator, request, success } from '../root.types'
import PatientMetrics from '../../application/models/patient.metrics/patient.metrics'
import QuestionnaireStatus from '../../application/models/questionnaires/questionnaire.status'
import PatientMetricsStatistics from '../../application/models/patient.metrics/patient.metrics.statistics'

export const INITIAL_STATE: IPatientMetricsState = {
    list: {
        data: [],
        loading: false,
        error: false,
        success: false,
    },
    statistics: {
        data: [],
        loading: false,
        error: false,
        success: false,
        paginator: {
            first: 0,
            rows: 30,
            page: 0,
            pageCount: 0,
            totalRecords: 0,
            search: {
                key: '',
                value: ''
            }
        }
    },
    statisticsChart: {
        data: [],
        loading: false,
        error: false,
        success: false,
        paginator: {
            first: 0,
            rows: 30,
            page: 0,
            pageCount: 0,
            totalRecords: 0,
            search: {
                key: '',
                value: ''
            }
        }
    },
    incomplete: {
        data: [],
        loading: false,
        error: false,
        success: false
    }
}

export const reset = () => INITIAL_STATE


export const loadRequest = (state: IPatientMetricsState = INITIAL_STATE) => {
    return {
        ...state,
        list: request(state.list)
    }
}

export const loadSuccess = (
    state: IPatientMetricsState = INITIAL_STATE,
    action: IActionType<{ data: PatientMetrics[] }>) => {
    const { data } = action.payload
    return {
        ...state,
        list: success({ ...state.list, data })
    }
}

export const loadFailure = (state: IPatientMetricsState = INITIAL_STATE) => {
    return {
        ...state,
        list: failure(state.list)
    }
}


export const loadIncompleteRequest = (state: IPatientMetricsState = INITIAL_STATE) => {
    return {
        ...state,
        incomplete: request(state.incomplete)
    }
}

export const loadIncompleteSuccess = (
    state: IPatientMetricsState = INITIAL_STATE,
    action: IActionType<{ data: QuestionnaireStatus[] }>) => {
    const { data } = action.payload
    return {
        ...state,
        incomplete: success({ ...state.incomplete, data })
    }
}

export const loadIncompleteFailure = (state: IPatientMetricsState = INITIAL_STATE) => {
    return {
        ...state,
        incomplete: failure(state.incomplete)
    }
}



export const loadStatisticsRequest = (
    state: IPatientMetricsState = INITIAL_STATE,
    action: IActionType<{ paginator: IPaginator }>) => {
    const { paginator } = action.payload
    const resultPaginator: IPaginator = paginator !== undefined ? paginator : state.statistics.paginator
    return {
        ...state,
        statistics: request({ ...state.statistics, paginator: resultPaginator })
    }
}

export const loadStatisticsSuccess = (
    state: IPatientMetricsState = INITIAL_STATE,
    action: IActionType<{ data: PatientMetricsStatistics[], headers: any }>) => {
    const { data, headers } = action.payload
    const totalRecords: number = parseInt(headers['x-total-count'] || 0, 10)
    const paginator: IPaginator = { ...state.statistics.paginator, totalRecords }
    return {
        ...state,
        statistics: success({ ...state.statistics, data, paginator })
    }
}

export const loadStatisticsFailure = (state: IPatientMetricsState = INITIAL_STATE) => {
    return {
        ...state,
        statistics: failure(state.statistics)
    }
}

export const loadMoreStatisticsRequest = (
    state: IPatientMetricsState = INITIAL_STATE,
    action: IActionType<{ paginator: IPaginator }>) => {
    const { paginator } = action.payload
    const resultPaginator: IPaginator = paginator !== undefined ? paginator : state.statistics.paginator
    return {
        ...state,
        statistics: request({ ...state.statistics, paginator: resultPaginator })
    }
}

export const loadMoreStatisticsSuccess = (
    state: IPatientMetricsState = INITIAL_STATE,
    action: IActionType<{ data: PatientMetricsStatistics[], headers: any }>) => {
    const { data, headers } = action.payload
    const totalRecords: number = parseInt(headers['x-total-count'] || 0, 10)
    const paginator: IPaginator = { ...state.statistics.paginator, totalRecords }
    const resultData: any[] = state.statistics.data.concat(data)
    return {
        ...state,
        statistics: success({ ...state.statistics, data: resultData, paginator })
    }
}

export const loadMoreStatisticsFailure = (state: IPatientMetricsState = INITIAL_STATE) => {
    return {
        ...state,
        statistics: failure(state.statistics)
    }
}


export const loadStatisticsHistoryRequest = (state: IPatientMetricsState = INITIAL_STATE) => {
    return {
        ...state,
        statisticsChart: request(state.statisticsChart)
    }
}

export const loadStatisticsHistorySuccess = (
    state: IPatientMetricsState = INITIAL_STATE,
    action: IActionType<{ data: PatientMetricsStatistics[], headers: any }>) => {
    const { data, headers } = action.payload
    const totalRecords: number = parseInt(headers['x-total-count'] || 0, 10)
    const paginator: IPaginator = { ...state.statisticsChart.paginator, totalRecords }
    return {
        ...state,
        statisticsChart: success({ ...state.statisticsChart, data, paginator })
    }
}

export const loadStatisticsHistoryFailure = (state: IPatientMetricsState = INITIAL_STATE) => {
    return {
        ...state,
        statisticsChart: failure(state.statisticsChart)
    }
}

const reducer: Reducer<IPatientMetricsState> = createReducer<IPatientMetricsState>(
    INITIAL_STATE, {
    [PatientMetricsTypes.RESET]: reset,
    [PatientMetricsTypes.LOAD_REQUEST]: loadRequest,
    [PatientMetricsTypes.LOAD_SUCCESS]: loadSuccess,
    [PatientMetricsTypes.LOAD_FAILURE]: loadFailure,
    [PatientMetricsTypes.LOAD_INCOMPLETE_REQUEST]: loadIncompleteRequest,
    [PatientMetricsTypes.LOAD_INCOMPLETE_SUCCESS]: loadIncompleteSuccess,
    [PatientMetricsTypes.LOAD_INCOMPLETE_FAILURE]: loadIncompleteFailure,

    [PatientMetricsTypes.LOAD_STATISTICS_REQUEST]: loadStatisticsRequest,
    [PatientMetricsTypes.LOAD_STATISTICS_SUCCESS]: loadStatisticsSuccess,
    [PatientMetricsTypes.LOAD_STATISTICS_FAILURE]: loadStatisticsFailure,

    [PatientMetricsTypes.LOAD_MORE_STATISTICS_REQUEST]: loadMoreStatisticsRequest,
    [PatientMetricsTypes.LOAD_MORE_STATISTICS_SUCCESS]: loadMoreStatisticsSuccess,
    [PatientMetricsTypes.LOAD_MORE_STATISTICS_FAILURE]: loadMoreStatisticsFailure,

    [PatientMetricsTypes.LOAD_STATISTICS_CHART_REQUEST]: loadStatisticsHistoryRequest,
    [PatientMetricsTypes.LOAD_STATISTICS_CHART_SUCCESS]: loadStatisticsHistorySuccess,
    [PatientMetricsTypes.LOAD_STATISTICS_CHART_FAILURE]: loadStatisticsHistoryFailure
})

export default reducer
